// utils
import dateFns from 'date-fns';
// consts
import { WORK_SCHEDULES } from 'app/config/work_schedules';
import { WORK_SHIFTS } from 'app/config/work_shifts';

// SCHEDULE_MAP é: { '1': '12x36', '2': '24x48', ... }
const SCHEDULE_MAP = Object.entries(WORK_SCHEDULES).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

/**
 * Cria um array de objetos (início e fim de turno/descanso) baseado na escala
 * de trabalho, limitado por uma data final (endDate) e um período máximo em
 * dias (period).
 *
 * @param {number} workScheduleValue O valor numérico (values de WORK_SCHEDULES) da escala de trabalho.
 * @param {Date} startDate A data e hora de início do primeiro turno.
 * @param {Date} endDate A data limite cujo nenhuma data gerada pode ultrapassar.
 * @param {number} period A quantidade máxima de dias corridos a partir de startDate.
 * @returns {{periodsForVisit: Array<{start: string, end: string}>, periodsForRest: Array<{start: string, end: string}>}} Um objeto com arrays de objetos representando
 * o início e fim de cada turno (periodsForVisit) e início e fim de cada descanso (periodsForRest).
 */
const _createSchedulePeriods = params => {
  let { startDate, workScheduleValue, workShift, endDate, period, selectionCompleted } = params;

  if (
    !startDate ||
    !workScheduleValue ||
    !workShift ||
    !endDate ||
    !period ||
    !selectionCompleted
  ) {
    return { periodsForVisit: [], periodsForRest: [] };
  }

  let startDateTime = dateFns.startOfDay(new Date(startDate));
  let endDateTime = dateFns.endOfDay(new Date(endDate));

  if (WORK_SHIFTS.MORNING === workShift) {
    startDateTime = dateFns.addHours(startDateTime, 7);
  } else if (WORK_SHIFTS.AFTERNOON === workShift) {
    throw new Error('turno inválido');
  } else if (WORK_SHIFTS.NIGHT === workShift) {
    startDateTime = dateFns.addHours(startDateTime, 19);
  }

  // ==============================================
  // --- Lógica de Validação e Cálculo do Ciclo ---
  // ==============================================
  const workSchedule = SCHEDULE_MAP[workScheduleValue];

  if (!workSchedule) {
    throw new Error(`Valor de escala de trabalho inválido: ${workScheduleValue}.`);
  }

  const scheduleMatch = workSchedule.match(/(\d+)x(\d+)/);

  if (!scheduleMatch) {
    throw new Error(`Erro de mapeamento: A escala '${workSchedule}' está em formato inválido.`);
  }

  // eslint-disable-next-line no-unused-vars
  const [_, workHoursStr, restHoursStr] = scheduleMatch;
  const workHours = parseInt(workHoursStr, 10);
  const restHours = parseInt(restHoursStr, 10);
  const cycleHours = workHours + restHours; // Horas entre o início de um turno e o próximo
  const MS_PER_HOUR = 3600000; // Milissegundos em uma hora
  const MS_PER_24HOURS = 86400000; // Milissegundos em um dia

  // ========================================
  // 1. Inicializa Variáveis e Limites
  // ========================================
  const periodsForVisit = [];
  const periodsForRest = [];
  let currentStartDateTime = startDateTime; // Cópia da data de início
  let currentEndDateTime = endDateTime; // Cópia da data de início

  // Limite máximo de tempo a partir do 'period' em dias
  const periodLimitTime = currentStartDateTime.getTime() + period * MS_PER_24HOURS;

  // O limite final será o menor entre a endDate e o período calculado
  const finalLimitTime = Math.min(currentEndDateTime.getTime(), periodLimitTime);

  // ========================================
  // 2. Gera as Datas com Limites (Refatorado)
  // ========================================
  while (currentStartDateTime.getTime() <= finalLimitTime) {
    const currentStartTimestamp = currentStartDateTime.getTime();

    // ----------------------------------------------------
    // PERÍODO DE TRABALHO (periodsForVisit)
    // ----------------------------------------------------
    const workEndTimestamp = currentStartTimestamp + workHours * MS_PER_HOUR; // Fim SEM limite (intervalo fechado)

    // O fim do turno é o início do descanso.
    // Garante que o turno não ultrapasse o limite (finalLimitTime)
    const actualWorkEndTimestamp = Math.min(workEndTimestamp, finalLimitTime);
    const actualWorkEndDate = new Date(actualWorkEndTimestamp); // Data de fim REAL

    if (currentStartTimestamp <= finalLimitTime) {
      periodsForVisit.push({
        start: currentStartDateTime.toISOString(),
        end: actualWorkEndDate.toISOString()
      });
    }

    // ----------------------------------------------------
    // PERÍODO DE DESCANSO (periodsForRest)
    // ----------------------------------------------------
    const restStartTimestamp = workEndTimestamp; // Início do descanso é o fim do trabalho
    const restEndTimestamp = restStartTimestamp + restHours * MS_PER_HOUR; // Fim SEM limite (intervalo fechado)

    // Garante que o descanso não ultrapasse o limite (finalLimitTime)
    const actualRestEndTimestamp = Math.min(restEndTimestamp, finalLimitTime);
    const actualRestEndDate = new Date(actualRestEndTimestamp); // Data de fim REAL

    // Adiciona o período de descanso apenas se o início não ultrapassar o limite final.
    if (restStartTimestamp < finalLimitTime) {
      periodsForRest.push({
        start: new Date(restStartTimestamp).toISOString(),
        end: actualRestEndDate.toISOString()
      });
    }

    // ----------------------------------------------------
    // PRÓXIMO CICLO
    // ----------------------------------------------------
    // Próximo início de turno
    const nextTime = currentStartTimestamp + cycleHours * MS_PER_HOUR;

    // Data de início para o próximo turno
    currentStartDateTime = new Date(nextTime);
  }

  return { periodsForVisit, periodsForRest };
};

export { _createSchedulePeriods };
